// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../../../node_modules/monaco-editor/monaco.d.ts" />
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, Injector, signal } from '@angular/core';
import { NavigationService } from '@fuse/components/navigation/navigation.service';
import { Navigation } from '@fuse/types';
import { getSelectedLanguage } from '@fuse/utils/translate';
import { DialogService, GatewayService, IUser, PrincipalService, UserUI } from '@ms/core';
import { TranslateService } from '@ngx-translate/core';
import { ReportService } from 'app/main/service/report.service';
import { environment } from 'environments/environment';
import { Observable, of, timer } from 'rxjs';
import { catchError, delayWhen, retryWhen, shareReplay, tap } from 'rxjs/operators';
import { ThemeLoaderService } from './theme-loader.service';

import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeKk from '@angular/common/locales/kk';
import localeRu from '@angular/common/locales/ru';
import localeTr from '@angular/common/locales/tr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebSocketService } from '@ms/core/service/web-socket.service';
import { equalPrimitives } from 'app/main/shared/utils';
import { VersionCheckService } from './version-check.service';

@Injectable({
    providedIn: 'root',
})
export class StartupService {
    _navigation: any;
    uaaUrl: string;
    theme: string = 'default-theme';
    showLoginDialog = signal(false);
    layoutLoading = signal(false);
    NavigationConfig = signal<any>(null, { equal: equalPrimitives });
    isAuthenticated = signal(false);
    userUI = signal<UserUI>(UserUI.NONE);
    contentContainer = signal<any>(undefined);
    reloadingAccount = signal(false);
    user = signal<IUser>(null, { equal: equalPrimitives });
    constructor(
        private http: HttpClient,
        private _NavigationService: NavigationService,
        private _translateService: TranslateService,
        private _reportService: ReportService,
        private versionCheck: VersionCheckService,
        private gatewayService: GatewayService,
        private themeLoader: ThemeLoaderService,
        private dialogService: DialogService,
        private principal: PrincipalService,
        private injector: Injector,
        private ws: WebSocketService
    ) {
        environment.injector = this.injector;
        setTimeout(() => {
            registerLocaleData(localeRu, 'ru');
            registerLocaleData(localeKk, 'kk');
            registerLocaleData(localeDe, 'de');
            registerLocaleData(localeTr, 'tr');
            registerLocaleData(localeFr, 'fr');
        }, 0);

        this.principal.state$.pipe(takeUntilDestroyed()).subscribe(state => {
            this.isAuthenticated.set(state.authenticated);
            this.user.set(state.user);
            this.userUI.set(state.user.ui);
        });
        this.ws.setup();

        // demo, test, live, aws
        if (window.location.href.startsWith('https')) {
            this.versionCheck.startVersionChecking('./assets/version.json', 30 * 60 * 1000);
        }
        this._reportService.init();
        this.gatewayService
            .systemParameters()
            .pipe(
                shareReplay(),
                retryWhen(errors => {
                    return errors.pipe(
                        delayWhen(() => timer(5000)),
                        tap(() => console.log('retrying...system'))
                    );
                }),
                catchError(e => of(e))
            )
            .subscribe(result => {
                if (result?.content) {
                    this.dialogService.close('gateway_error_message');
                    console.log('SERVER-CONFIG', result);
                    environment.resetPasswordUrl = `${result.content.productLoginUrls.find(p => p.key == 'uaa').value}/reset/request`;
                    const { debug } = result.content;
                    environment.production = debug != true;
                    environment.development = debug == true;
                    environment.traceErrorLog = result.content.traceErrorLog;
                    //environment.traceLog = true;
                    environment.appUrl = result.content.productLoginUrls.find(p => p.key == 'campus').value;
                    // environment.development = false;
                    gatewayService.socialLoginConfigInit();
                    if (environment.production) {
                        window.console.time = () => {};
                        window.console.log = () => {};
                        window.console.timeEnd = () => {};
                        window.console.debug = () => {};
                        window.console.info = () => {};
                    }

                    this.uaaUrl = result.content.productLoginUrls.find(p => p.key == 'uaa').value;
                    //console.log('Environment', environment);
                    this._NavigationService.userLoggedIn();
                }
            });
        this.setDefaultTheme();
    }

    setDefaultTheme() {
        if (localStorage.getItem('theme')) {
            this.theme = localStorage.getItem('theme');
        }
        this.themeLoader.init(this.theme);
    }

    init(isUserLogin: boolean) {
        if (!this.navigation) {
            this.navigation = [];
        }
        this._NavigationService.register('main', this.navigation);
        // Add languages
        this._translateService.addLangs(['en', 'kk', 'ru', 'de', 'tr', 'fr', 'en-us']);
        // Set the default language
        this._translateService.setDefaultLang('en');

        // Get selected language and load it
        this._translateService.use(getSelectedLanguage(this._translateService));

        const browserLang = this._translateService.getBrowserLang();
        console.log('Browser Lang=', browserLang);
        this._translateService.use(browserLang.match(/en|kk|ru|de|tr|fr|en-us/) ? browserLang : 'en');
        // console.log('Environment', environment);
        console.log('Logged', isUserLogin);
        if (isUserLogin) {
            this.clearNavigation();
            //sessionStorage.setItem('navigation-ams', JSON.stringify(this.navigation));
            // Get default navigation
            //console.log('App-Nav', this.navigation);
            // Register the navigation to the service
            //this._NavigationService.register('main', this.navigation);
            // Set the main navigation as our current navigation
        } else {
            if (environment.production) {
                sessionStorage.removeItem('navigation-ams');
                this.navigation = [];
                this.buildNavigation();
            }
        }
        this._NavigationService.setCurrentNavigation('main');
    }

    clearNavigation() {
        this.navigation = (JSON.parse(sessionStorage.getItem('navigation-ams')) as Navigation[]) || [];
        console.log('Nav.Dev.Mode:', this.navigation);
        if (this.navigation.length > 0) {
            this._NavigationService.register('main', this.navigation);
            this._NavigationService.setCurrentNavigation('main');
        }
    }

    buildNavigation() {
        // navigation is empty or not loaded yet
        if (!this.navigation || this.navigation.length == 0) {
            this.loadNavigation().subscribe(result => {
                this.navigation = [
                    {
                        id: '506f1f77bcf86cd799439011',
                        serviceId: '600',
                        title: 'Dashboard',
                        translate: 'NAV.DASHBOARD',
                        type: 'group',
                        url: '/',
                        publicPrivilege: true,
                        active: true,
                        hidden: false,
                        children: result.body,
                    },
                ];
                sessionStorage.setItem('navigation-ams', JSON.stringify(this.navigation));
                // Get default navigation
                //console.log('App-Nav', this.navigation);

                // Register the navigation to the service
                this._NavigationService.register('main', this.navigation);

                // Set the main navigation as our current navigation
                this._NavigationService.setCurrentNavigation('main');
            });
        }
    }

    loadNavigation(): Observable<HttpResponse<any>> {
        return this.http.get<Navigation[]>('uaa/api-public/navigation-ams', {
            observe: 'response',
        });
    }

    get navigation() {
        return this._navigation;
    }

    set navigation(value: any) {
        this._navigation = value;
    }
}
